import React from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { useLanguage } from "../../i18n/LanguageContext";
import { LiveLearnTicker } from "./LiveLearnTicker";
import LivePROTicker from "./LivePROTicker";

const TRANSLATIONS = {
  es: {
    tag: "// RE₿EL TOKEN · $R₿L",
    title: "El token deflacionario del ecosistema",
    subtitle: "RE₿EL Academy en Base L2",
    supply: "Supply total: 1,000,000,000 · Sin minting · Solo deflación",
    pill1Title: "🔥 Deflación",
    pill1Text: "5% quemado en cada pago PRO y Shadow Market",
    pill2Title: "💎 Utilidad real",
    pill2Text: "Vault · PRO · Market · DAO",
    pill3Title: "🎓 Educación",
    pill3Text: "El token que te enseña a invertir",
    cta: "Ver token $R₿L",
  },
  en: {
    tag: "// RE₿EL TOKEN · $R₿L",
    title: "The deflationary token of the ecosystem",
    subtitle: "RE₿EL Academy on Base L2",
    supply: "Total supply: 1,000,000,000 · No minting · Only deflation",
    pill1Title: "🔥 Deflation",
    pill1Text: "5% burned on every PRO payment & Shadow Market purchase",
    pill2Title: "💎 Real utility",
    pill2Text: "Vault · PRO · Market · DAO",
    pill3Title: "🎓 Education",
    pill3Text: "The token that teaches you to invest",
    cta: "View $R₿L token",
  },
  sk: {
    tag: "// RE₿EL TOKEN · $R₿L",
    title: "Deflačný token ekosystému",
    subtitle: "RE₿EL Academy na Base L2",
    supply: "Celková zásoba: 1 000 000 000 · Žiadne mintovanie · Len deflácia",
    pill1Title: "🔥 Deflácia",
    pill1Text: "5% spálené pri každej PRO platbe a nákupe v Shadow Markete",
    pill2Title: "💎 Reálna utilita",
    pill2Text: "Vault · PRO · Market · DAO",
    pill3Title: "🎓 Vzdelávanie",
    pill3Text: "Token ktorý ťa učí investovať",
    cta: "Zobraziť token $R₿L",
  }, de: { tag: "// RE₿EL TOKEN · $R₿L", title: "Der deflationäre Token des Ökosystems", subtitle: "RE₿EL Academy auf Base L2", supply: "Gesamtangebot: 1.000.000.000 · Kein Minting · Nur Deflation", pill1Title: "🔥 Deflation", pill1Text: "5% verbrannt bei jeder PRO- und Shadow Market-Zahlung", pill2Title: "💎 Echter Nutzen", pill2Text: "Vault · PRO · Market · DAO", pill3Title: "🎓 Bildung", pill3Text: "Der Token, der dir das Investieren beibringt", cta: "Token $R₿L ansehen" }, "zh-CN": { tag: "// RE₿EL TOKEN · $R₿L", title: "生态系统的通缩代币", subtitle: "RE₿EL Academy 在 Base L2", supply: "总供应量：1,000,000,000 · 无增发 · 仅通缩", pill1Title: "🔥 通缩", pill1Text: "每次 PRO 和 Shadow Market 支付销毁 5%", pill2Title: "💎 实际效用", pill2Text: "Vault · PRO · Market · DAO", pill3Title: "🎓 教育", pill3Text: "教你投资的代币", cta: "查看代币 $R₿L" },
};

export const RBLSection = () => {
  const navigate = useNavigate();
  const { lang } = useLanguage();
  const t = TRANSLATIONS[lang] || TRANSLATIONS.en || TRANSLATIONS.es;

  return (
    <section
      className="relative w-full overflow-hidden py-20 md:py-28 bg-black"
      data-testid="rbl-section"
    >
      {/* Smooth gradient bridge from black (top) into the section */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-40"
        style={{
          background:
            "linear-gradient(to bottom, #000000 0%, rgba(0,0,0,0.6) 60%, transparent 100%)",
        }}
      />
      {/* Subtle edge glows (orange / violet) — kept very soft so they don't tint the section */}
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(109,40,217,0.08),transparent_60%)]" />

      <div className="relative z-10 mx-auto flex max-w-6xl flex-col items-center px-6 text-center md:px-12">
        {/* 3D coin video loop */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          className="mb-8 rbl-coin-wrapper"
        >
          <video
            autoPlay
            loop
            muted
            playsInline
            preload="auto"
            src="/videos/rbl-coin.mp4"
            className="rbl-coin-video h-[220px] w-[220px] md:h-[300px] md:w-[300px] object-contain"
            data-testid="rbl-coin-video"
          />
        </motion.div>

        {/* Tag */}
        <motion.span
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="mb-4 inline-block text-[12px] font-semibold uppercase tracking-[0.25em]"
          style={{ color: "#f97316" }}
          data-testid="rbl-section-tag"
        >
          {t.tag}
        </motion.span>

        {/* Title */}
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, delay: 0.15 }}
          className="font-display max-w-3xl text-balance text-3xl font-bold leading-tight text-white sm:text-4xl md:text-[36px]"
          data-testid="rbl-section-title"
        >
          {t.title}
        </motion.h2>

        {/* Subtitle */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.8, delay: 0.25 }}
          className="mt-3 text-base md:text-lg"
          style={{ color: "#9ca3af" }}
          data-testid="rbl-section-subtitle"
        >
          {t.subtitle}
        </motion.p>

        {/* Pills */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9, delay: 0.35 }}
          className="mt-10 grid w-full max-w-4xl grid-cols-1 gap-4 md:grid-cols-3"
        >
          {[
            { title: t.pill1Title, text: t.pill1Text, testid: "rbl-pill-deflation" },
            { title: t.pill2Title, text: t.pill2Text, testid: "rbl-pill-utility" },
            { title: t.pill3Title, text: t.pill3Text, testid: "rbl-pill-education" },
          ].map((p) => (
            <div
              key={p.testid}
              className="rounded-xl px-5 py-5 text-left transition-all hover:-translate-y-0.5"
              style={{
                backgroundColor: "#111118",
                border: "1px solid rgba(109,40,217,0.3)",
              }}
              data-testid={p.testid}
            >
              <div className="text-sm font-bold text-white">{p.title}</div>
              <div className="mt-1.5 text-sm" style={{ color: "#9ca3af" }}>
                {p.text}
              </div>
            </div>
          ))}
        </motion.div>

        {/* Supply text */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9, delay: 0.5 }}
          className="mt-10 text-[13px]"
          style={{ color: "#6b7280" }}
          data-testid="rbl-section-supply"
        >
          {t.supply}
        </motion.div>

        {/* Live Learn-to-Earn ticker */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9, delay: 0.55 }}
          className="mt-6 w-full"
        >
          <LiveLearnTicker />
          <LivePROTicker />
        </motion.div>

        {/* CTA — same style as hero secondary button */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.9, delay: 0.6 }}
          className="mt-8"
        >
          <button
            onClick={() => navigate("/shadow/tokens")}
            className="group inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-7 py-3.5 text-sm font-semibold text-white backdrop-blur-md transition-all hover:-translate-y-0.5 hover:border-orange-400/50 hover:bg-white/10 hover:shadow-[0_0_30px_rgba(249,115,22,0.35)] md:text-base"
            data-testid="rbl-section-cta"
          >
            {t.cta}
            <span className="transition-transform group-hover:translate-x-1">→</span>
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default RBLSection;
